import { css, cva } from '@/styled-system/css';

/** Native destination link layered beneath the card's interactive controls. */
export const cardLink = css({
  position: 'absolute',
  inset: 0,
  borderRadius: 'md',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  zIndex: 'zIndexBase',
  _focusVisible: {
    outlineWidth: 'thick',
    outlineStyle: 'solid',
    outlineColor: 'border.accent.brand.hero',
    outlineOffset: '50',
  },
});

export const cardForeground = css({
  position: 'relative',
  pointerEvents: 'none',
  zIndex: 'zIndexStep',
  '& :is(a, button, [role="button"])': {
    pointerEvents: 'auto',
  },
  '& [role="tab"]': {
    pointerEvents: 'none',
  },
});

export const contactActions = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tighter',
  // Keep in step with the theme's `PropertyCard.DataWrapper.large.padding.horz`
  // (`tighter`) so the buttons align with the data rows above; lint forbids
  // referencing the component token directly.
  paddingInline: 'tighter',
  paddingBlockEnd: 'tight',
  width: '100%',
  '& > *': {
    flexBasis: 'calc((100% - {spacing.tighter}) / 2)',
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 0,
  },
  '& > :only-child': {
    flexBasis: '100%',
  },
});

// nowrap: "Message again?" wraps to two lines on narrow cards otherwise.
export const messageLabelStyles = css.raw({
  whiteSpace: 'nowrap',
  '@container listingCard (min-width: 22em)': {
    fontSize: { base: 'body-sm', lg: 'body' },
  },
});

// Below 22em, the full phone number starts to wrap beside the message action.
export const compactPhoneAction = css({
  display: 'flex',
  flex: 1,
  '@container listingCard (min-width: 22em)': {
    display: { base: 'flex', lg: 'none' },
  },
});

export const fullPhoneAction = css({
  display: 'none',
  flex: 1,
  '@container listingCard (min-width: 22em)': {
    display: { base: 'none', lg: 'flex' },
  },
});

/**
 * Card photo fill, including the hover-zoom resting state (scale 1 + the
 * settle-back ease-in transition — see the `SLIDE_ZOOM_TRANSITION` note).
 * The zoom only ever triggers on carousel slide images (`cardContainer`'s
 * hover rule targets `[Slide] img`), so the resting transform/transition are
 * inert on the static photo and placeholder uses of this class.
 */
export const photoImageFill = css({
  width: '100%',
  height: '100%',
  transform: 'scale(1)',
  transformOrigin: 'center',
  transition: {
    _motionReduce: 'none',
    _motionSafe: 'transform 300ms ease-in',
  },
});

/** Price line with the total-price / fees indicator inline beside it (`dataArea1`). */
export const priceRow = css({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'tightest',
  minWidth: 0,
  // A little space below the price before the beds/baths line.
  marginBlockEnd: 'tightest',
});

export const priceValue = css({
  textStyle: 'heading-sm',
});

/** Non-interactive "Total price" badge (when no tooltip copy is supplied). */
export const totalPriceBadge = css({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 'tightest',
});

/**
 * Filled circle-check: brand-primary fill with a white stroked check. Lucide
 * hardcodes `fill="none" stroke="currentColor"` as SVG attrs, so override both
 * via CSS (CSS beats those attrs).
 */
export const totalPriceCheckIcon = css({
  color: 'text.accent.brand.hero',
  fill: 'currentColor',
  flexShrink: 0,
  stroke: 'white',
});

export const totalPriceLabel = css({
  color: 'text.accent.brand.hero',
  '&&': {
    fontSize: 'body',
    fontWeight: 'bold',
    lineHeight: 'body',
  },
});

export const feesApply = css({
  color: 'text.subtle',
  textStyle: 'body',
});

/** Row wrapper for the beds/baths/units line (`minWidth: 0` lets it truncate in the grid). */
export const homeDetailsRow = css.raw({
  alignItems: 'center',
  display: 'flex',
  gap: 'tightest',
  minWidth: 0,
  '&&&': {
    marginBlockEnd: 'tightest',
  },
});

/**
 * Beds/baths/sqft kept to one line, truncating only at the full card width.
 * Home details are rendered manually (no PropertyCard.HomeDetails) so the ·
 * separator is a literal character in JSX rather than a CSS override.
 */
export const homeDetailsArea = css({
  minWidth: 0,
  overflow: 'hidden',
  textStyle: 'body',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const homeDetailValue = css({
  textStyle: 'body-bold',
});

/** Identity line (`dataArea3`): property-type icon + name/address + building cue. */
export const identityRow = css({
  alignItems: 'center',
  display: 'flex',
  gap: 'tighter',
  minWidth: 0,
  textStyle: 'body',
});

export const identityLabelGroup = css({
  alignItems: 'center',
  display: 'flex',
  gap: 'tightest',
  minWidth: 0,
});

/** City/state/zip appended inline on the identity line, after the building name. */
export const identityAddress = css({
  color: 'text.subtle',
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

export const identityIcon = css({
  color: 'text.subtle',
  flexShrink: 0,
});

export const identityLabel = css({
  color: 'text.subtle',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

/** "View units →" affordance shown when a card opens a multi-unit building page. */
export const buildingCue = css({
  alignItems: 'center',
  color: 'text.accent.brand.hero',
  display: 'inline-flex',
  fontWeight: 'bold',
  gap: 'micro.default',
  whiteSpace: 'nowrap',
});

/**
 * Surgical overrides for values Constellation hardcodes (not driven by a
 * token, so they can't be themed in `panda.config.ts`).
 *
 * Body bg and DataWrapper horizontal padding are *not* here — both are
 * token-backed and set by Constellation's `hotpads-legacy` theme:
 *  - `colors.PropertyCard.Body.bg.default` → transparent
 *  - `spacing.PropertyCard.DataWrapper.{large,small}.padding.horz` → `tighter`
 *
 * Photo bottom corners can't be themed: Constellation's photo recipe writes
 * `borderEndStartRadius: '0'` / `borderEndEndRadius: '0'` directly into the
 * `large` / `small` variants, and the carousel-clip rule writes a literal
 * `{radii.PropertyCard.Body} {radii.PropertyCard.Body} 0px 0px` shorthand —
 * no token sits between either of those and the recipe. So we override via
 * a descendant selector on `data-c11n-component`. If Constellation later
 * exposes a `radii.PropertyCard.Photo.{bottomStart,bottomEnd}` token, this
 * block can move to the theme too.
 */
export const cardOverrides = css.raw({
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  pointerEvents: 'none',
  '& :is(a, button, [role="button"])': {
    pointerEvents: 'auto',
  },
  '& [data-c11n-component="PropertyCard.PhotoWrapper"], & [data-c11n-component="PropertyCard.DataWrapper"], & [data-c11n-component="PropertyCard.FlexArea"]':
    {
      position: 'relative',
      zIndex: 'zIndexStep',
    },
  '& [data-c11n-component="PropertyCard.PhotoBody"]': {
    borderEndStartRadius: 'obj.default',
    borderEndEndRadius: 'obj.default',
  },
  '& [data-c11n-component="PhotoCarousel.Root"]': {
    borderRadius: 'obj.default',
  },
  '& [data-c11n-component="PropertyCard.PhotoFooter"]': {
    justifySelf: 'flex-start',
    marginBlock: 'tighter',
    marginInline: 'tighter',
  },
  '& [data-c11n-component="PropertyCard.BadgeArea"]': {
    marginBlockStart: '150',
  },
});

export const interactiveCarouselRoot = css.raw({
  pointerEvents: 'auto',
});

/**
 * Chip treatment for the carousel arrows, applied through each button's
 * `css` prop. Constellation's nav-button recipe merges the `css` prop over its
 * own raw styles with a shallow spread, so same-string keys replace theirs
 * wholesale: `'&&'` (their full-height + onHero color), `'&&&:active,
 * &&&:hover'` (their transparent interaction pin), the Icon drop-shadow, and
 * the inset each side anchors to.
 *
 * The chip wears the same scrim treatment as the carousel dots — identical
 * tokens to the Dots/Dot recipes — so the arrows read as one family with the
 * dots (same WCAG 1.4.11 contrast-by-design) and stay visually distinct from
 * the white save/share action bar above.
 */
const carouselButtonRecipe = cva({
  base: {
    alignItems: 'center',
    backgroundColor: 'bg.transparent.impact-fixed',
    borderRadius: 'ellipse',
    display: 'flex',
    justifyContent: 'center',
    padding: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    '&&&:active, &&&:hover': {
      backgroundColor: 'bg.transparent.impact-fixed',
      color: 'icon.onImpact.neutral-fixed',
    },
  },
  variants: {
    size: {
      large: {
        '&&': {
          color: 'icon.onImpact.neutral-fixed',
          height: '700',
        },
        minHeight: '700',
        minWidth: '700',
        width: '700',
        '& > [data-c11n-component="Icon"]': {
          filter: 'none',
          height: '400',
          width: '400',
        },
      },
      compact: {
        '&&': {
          color: 'icon.onImpact.neutral-fixed',
          height: '600',
        },
        minHeight: '600',
        minWidth: '600',
        width: '600',
        '& > [data-c11n-component="Icon"]': {
          filter: 'none',
          height: '300',
          width: '300',
        },
      },
    },
    side: {
      previous: { insetInlineEnd: 'auto' },
      next: { insetInlineStart: 'auto' },
    },
  },
  compoundVariants: [
    { size: 'large', side: 'previous', css: { insetInlineStart: 'tighter' } },
    { size: 'large', side: 'next', css: { insetInlineEnd: 'tighter' } },
    { size: 'compact', side: 'previous', css: { insetInlineStart: 'tightest' } },
    { size: 'compact', side: 'next', css: { insetInlineEnd: 'tightest' } },
  ],
});

export const carouselPreviousButtonLarge = carouselButtonRecipe.raw({ size: 'large', side: 'previous' });
export const carouselNextButtonLarge = carouselButtonRecipe.raw({ size: 'large', side: 'next' });
export const carouselPreviousButtonCompact = carouselButtonRecipe.raw({ size: 'compact', side: 'previous' });
export const carouselNextButtonCompact = carouselButtonRecipe.raw({ size: 'compact', side: 'next' });

// Hover-zoom on the photo. Constellation exposes no easing/duration tokens, and
// its only transform transition token (`PhotoCarousel.Slide`) uses a
// slide-tuned curve that snaps almost instantly — wrong feel for a zoom. So we
// set an explicit `transform 300ms` transition (SLIDE_ZOOM_TRANSITION): ease-out
// as the pointer enters (this hover rule), ease-in as it settles back (the
// resting rule lives on `photoImageFill`, the slide image's own class). Both
// are gated to `_motionSafe` so reduced-motion users get an instant change.
const slideZoomHover = css.raw({
  transform: 'scale(1.05)',
  transition: {
    _motionReduce: 'none',
    _motionSafe: 'transform 300ms ease-out',
  },
});

/**
 * Size-only Dots override — never restyle them: Constellation's own Dots/Dot
 * recipes paint a centered capsule scrim + calibrated dot colors that
 * guarantee WCAG 1.4.11 contrast against arbitrary photos and a visible
 * _focusWithin indicator; earlier color/positioning overrides broke both.
 * 20px (vs the recipe's 12px minimum) makes the capsule share edges and
 * center with the 20px Verified badge pill across the photo bottom, both on
 * Constellation's default 8px inset.
 */
export const carouselDots = css.raw({
  // Same `minHeight` key as Constellation's Dots styles so the css-prop merge
  // replaces theirs (a `minBlockSize` alias would lose on cascade order).
  minHeight: '500',
  // Wider than the recipe's `tightest` so the capsule's side padding balances
  // the taller 20px profile.
  paddingInline: 'tighter',
});

/** Lets a data-area grid cell shrink below content size so its row can truncate. */
export const dataAreaShrink = css.raw({
  minWidth: 0,
});

export const cardContainer = css({
  containerName: 'listingCard',
  containerType: 'inline-size',
  position: 'relative',
  isolation: 'isolate',
  width: '100%',
  // Card-wide hover zoom; the arrow reveal is Constellation's own (photo hover).
  _hoverMouse: {
    '&:hover [data-c11n-component="PhotoCarousel.Slide"] img': slideZoomHover,
  },
});

/**
 * The vertical (large / compact) card treatment — Tier 2 overrides for values
 * Constellation hardcodes (not driven by a token, so they can't be themed):
 * the pointer-events layering under the card link, photo geometry (4:3 ratio,
 * rounded clip that also handles the hardcoded square photo bottom corners —
 * required for the hover zoom anyway), and the footer/save-area resets. The
 * `appearance` variants hold only the deltas: grid rows and the compact
 * data-wrapper margin.
 *
 * Not here: arrow chip styling and the dots offset ride the elements' own
 * `css` props (`carouselButtonRecipe`, `carouselDots`), arrow hide/reveal is
 * Constellation's own, and token-backed styling lives in Constellation's
 * `hotpads-legacy` theme: PropertyCard Body bg → transparent,
 * DataWrapper padding + between-dataItems gap, FlexArea padding.
 */
const verticalCardRecipe = cva({
  base: {
    width: '100%',
    maxWidth: 'none',
    minWidth: 0,
    pointerEvents: 'none',
    // Carousel dots are excluded: Constellation deliberately keeps the tablist
    // pointer-inert (dots are keyboard/indicator affordances well below the
    // WCAG 2.5.8 24px target size); re-enabling clicks on them regressed that.
    '& :is(a, button, [role="button"])': {
      pointerEvents: 'auto',
    },
    '& [role="tab"]': {
      pointerEvents: 'none',
    },
    '& [data-c11n-component="PropertyCard.DataWrapper"], & [data-c11n-component="PropertyCard.FlexArea"], & [data-c11n-component="PropertyCard.PhotoWrapper"]':
      {
        position: 'relative',
        zIndex: 'zIndexStep',
      },
    '& [data-c11n-component="PropertyCard.PhotoFooter"]': {
      justifySelf: 'flex-start',
      marginBlock: 'tighter',
      marginInline: 'tighter',
    },
    // No BadgeArea margin override: the accent badge sits on Constellation's
    // own 8px inset, matching its left inset and the other photo-corner
    // overlays (the old +6px `marginBlockStart: '150'` offset was removed
    // deliberately).
    // The rounded clip tracks the same token Constellation's Body recipe
    // paints (`obj.default`), so a Tier 1 radius change stays in sync.
    '& [data-c11n-component="PropertyCard.PhotoWrapper"]': {
      aspectRatio: '4 / 3',
      borderRadius: 'obj.default',
      minBlockSize: 'auto',
      overflow: 'hidden',
    },
    '& [data-c11n-component="PhotoCarousel.PreviousButton"]:disabled, & [data-c11n-component="PhotoCarousel.NextButton"]:disabled':
      {
        opacity: 0,
        pointerEvents: 'none',
      },
    // Arrow hide/reveal is Constellation's own (transparent at rest, revealed
    // on photo hover via its Root recipe's opacity + zIndex flip). This mirrors
    // that reveal when keyboard focus is inside the carousel, for hybrid users.
    '& [data-c11n-component="PhotoCarousel.Root"]:focus-within > [data-c11n-component="PhotoCarousel.NavControls"]': {
      opacity: 1,
      zIndex: 'zIndexBase',
    },
    '& [data-c11n-component="PropertyCard.SaveArea"]': {
      marginRight: 'none',
      marginTop: 'none',
    },
    // Center the options trigger on the price line (`priceValue`'s heading-sm) without Constellation's upward nudge; X tracks the theme's DataWrapper `padding.horz`.
    '& [data-c11n-component="PropertyCard.ActionArea"]': {
      alignItems: 'center',
      blockSize: '{lineHeights.heading-sm}',
      display: 'flex',
      justifyContent: 'flex-end',
      transform: 'translateX({spacing.tighter})',
    },
  },
  variants: {
    appearance: {
      large: {
        '& [data-c11n-component="PropertyCard.Body"]': {
          // Photo / data / flex rows size to content. Constellation's default
          // large grid uses a fixed photo-row height, which the 4:3
          // aspect-ratio photo overflows — pushing it down over the data
          // area. Auto rows track the real photo height instead.
          gridTemplateRows: 'auto auto auto',
        },
      },
      compact: {
        '& [data-c11n-component="PropertyCard.Body"]': {
          // Photo + data rows size to content (see the large variant note).
          gridTemplateRows: 'auto auto',
        },
        '& [data-c11n-component="PropertyCard.DataWrapper"]': {
          marginBlockStart: 'tightest',
        },
      },
    },
    loading: {
      true: {
        '& [data-c11n-component="PropertyCard.DataWrapper"]': {
          // Floor must equal the resolved large card's data-area height (measured 112px) or the skeleton→content Suspense swap shifts layout (CLS).
          // eslint-disable-next-line hotpads/no-magic-numbers -- C11n has no public token or prop for the resolved card data height
          minBlockSize: '112px',
        },
      },
    },
  },
});

export const largeCardOverrides = verticalCardRecipe.raw({ appearance: 'large' });
export const largeCardSkeletonOverrides = verticalCardRecipe.raw({ appearance: 'large', loading: true });
export const compactCardOverrides = verticalCardRecipe.raw({ appearance: 'compact' });

/**
 * Horizontal appearance leans on Constellation's own photo-left / data-right
 * layout and `horizontal` DataWrapper tokens (the hotpads-legacy theme leaves them at Constellation's defaults)
 * instead of the SRP-tuned overrides the vertical cards use — those made it
 * cramped. Only the read-only Share preview uses this today, so it's
 * deliberately minimal: full width, rounded photo, and data padding widened
 * from Constellation's `tight` to `default` with the vertical padding dropped
 * in favor of centering.
 */
export const horizontalCardOverrides = css.raw({
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  // Body radius comes from Constellation (`obj.default`); only the clip is ours.
  '& [data-c11n-component="PropertyCard.Body"]': {
    overflow: 'hidden',
  },
  // Square photo with all four corners rounded (Constellation uses a stretched,
  // left-rounded photo by default in the horizontal layout).
  '& [data-c11n-component="PropertyCard.PhotoWrapper"]': {
    alignSelf: 'center',
    aspectRatio: '1 / 1',
    borderRadius: 'obj.default',
    minBlockSize: 'auto',
    overflow: 'hidden',
  },
  '& [data-c11n-component="PropertyCard.DataWrapper"]': {
    alignContent: 'center',
    paddingBlock: 0,
    paddingInline: 'default',
  },
  '& [data-c11n-component="PropertyCard.SaveArea"]': {
    marginRight: 'none',
    marginTop: 'none',
  },
});
